import KeyboardIcon from "@mui/icons-material/KeyboardOutlined";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useMemo } from "react";

import type { DispatchAction } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { dispatchShortcutEntries, eventMatchesBinding } from "@/hooks/dispatch/dispatchKeymap";
import { zinc } from "@/ui/tokens";
import { ShortcutKeys } from "./DispatchShortcutsBar";

/**
 * Every shortcut in one vertical list, dropping from the header's keyboard button as the classic driver list's does.
 *
 * One row per entry of the helper (`dispatchShortcutEntries`), in its order, label left and keys right, All shortcuts
 * last: the bar and this list read the same entries, so they never disagree. While it is open the page's keys stand
 * down (`useDispatchShortcuts`), so the list closes on its own key itself, as it does on Escape or a click away. Focus
 * is not handed back to whatever opened it, so the next key is a page shortcut rather than a press of that button.
 */
export default function DispatchShortcutsList({
  open,
  anchorEl,
  keymap,
  onClose,
}: {
  open: boolean;
  /** The header's keyboard button. */
  anchorEl: HTMLElement | null;
  keymap: Record<DispatchAction, string>;
  onClose: () => void;
}) {
  const entries = useMemo(() => dispatchShortcutEntries(keymap), [keymap]);
  const closeOnOwnKey = (event: React.KeyboardEvent) => {
    if (keymap.showShortcuts === "" || !eventMatchesBinding(event, keymap.showShortcuts)) return;
    event.preventDefault();
    onClose();
  };
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      onKeyDown={closeOnOwnKey}
      disableRestoreFocus
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            width: 340,
            maxWidth: "calc(100vw - 16px)",
            maxHeight: "calc(100vh - 96px)",
            bgcolor: zinc[800],
            border: `1px solid ${zinc[600]}`,
            backgroundImage: "none",
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            mt: 0.5,
          },
        },
      }}
    >
      <Box data-sb={TestIds.dispatch.shortcutsList} sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <KeyboardIcon sx={{ color: "white", fontSize: 20 }} />
          <Typography sx={{ color: "white", fontSize: "0.9375rem", fontWeight: 600 }}>Keyboard shortcuts</Typography>
        </Box>
        {entries.map((entry) => (
          <Box
            key={entry.action}
            data-sb={TestIds.dispatch.shortcutEntry}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 0.5 }}
          >
            <Typography data-sb={TestIds.dispatch.shortcutLabel} sx={{ color: zinc[300], fontSize: "0.8125rem" }}>
              {entry.label}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <ShortcutKeys keys={entry.keys} />
            </Box>
          </Box>
        ))}
      </Box>
    </Popover>
  );
}
